import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  Button,
} from "antd";
import {
  CalendarOutlined,
  ReloadOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getOpsFounderReport } from "../services/dashboardService.js";
import SeasonOpsBrief from "../../pricing/components/SeasonOpsBrief.jsx";

const { Title, Text, Paragraph } = Typography;

const ShellRoot = styled.div`
  min-width: 0;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SlotCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const KpiTile = styled.div`
  border: 1px solid #e6efed;
  border-radius: 10px;
  padding: 14px;
  height: 100%;
  background: #fff;

  .kpi-value {
    font-size: 24px;
    font-weight: 700;
    color: #005249;
    line-height: 1.15;
  }

  .kpi-label {
    color: #5f6b6a;
    font-size: 12px;
    margin-top: 4px;
  }
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-end;
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e6efed;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .filter-label {
    font-size: 12px;
    color: #5f6b6a;
    font-weight: 600;
  }
`;

const ChecklistItem = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #333;

  &:last-child {
    border-bottom: none;
  }
`;

const ChartViewport = styled.div`
  overflow-x: auto;
`;

const InlineMeta = styled.div`
  margin-top: 8px;
`;

const ChartSvg = styled.svg`
  display: block;
  width: 100%;
  min-width: 520px;
  height: auto;
`;

function toYmd(value) {
  if (!value) return null;
  return dayjs(value).format("YYYY-MM-DD");
}

function defaultAsOf() {
  return dayjs();
}

function defaultRange(asOf) {
  const end = dayjs(asOf);
  return [end.subtract(29, "day"), end];
}

function priorityColor(priority) {
  if (priority === "P0") return "orange";
  if (priority === "P1") return "blue";
  return "default";
}

function SimpleTrendChart({ trend }) {
  if (!trend?.days?.length) {
    return <Empty description="No trend data" />;
  }

  const width = 720;
  const height = 220;
  const pad = { top: 16, right: 16, bottom: 36, left: 36 };
  const seriesDefs = [
    { key: "leads", color: "#005249", label: "Leads" },
    { key: "quotes", color: "#1890ff", label: "Quotes" },
    { key: "orders", color: "#52c41a", label: "Orders" },
  ];
  const allValues = seriesDefs.flatMap((s) => trend[s.key] || []);
  const max = Math.max(1, ...allValues);
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const n = trend.days.length;

  const point = (index, value) => {
    const x = pad.left + (n <= 1 ? plotW / 2 : (index / (n - 1)) * plotW);
    const y = pad.top + plotH - (Number(value) / max) * plotH;
    return `${x},${y}`;
  };

  return (
    <ChartViewport>
      <ChartSvg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Created volume trend">
        {seriesDefs.map((series) => {
          const values = trend[series.key] || [];
          const points = values.map((v, i) => point(i, v)).join(" ");
          return (
            <polyline
              key={series.key}
              fill="none"
              stroke={series.color}
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
              points={points}
            />
          );
        })}
        {trend.days.map((label, index) => {
          const x =
            pad.left + (n <= 1 ? plotW / 2 : (index / (n - 1)) * plotW);
          if (index % 2 !== 0 && index !== n - 1) return null;
          return (
            <text
              key={label}
              x={x}
              y={height - 10}
              textAnchor="middle"
              fill="#8c8c8c"
              fontSize="11"
            >
              {label}
            </text>
          );
        })}
      </ChartSvg>
      <Space size="middle" style={{ marginTop: 8 }}>
        {seriesDefs.map((s) => (
          <Text key={s.key} style={{ color: s.color, fontSize: 12 }}>
            ● {s.label}
          </Text>
        ))}
      </Space>
    </ChartViewport>
  );
}

const OpsFounderReportShell = ({ canView = true }) => {
  const [asOf, setAsOf] = useState(() => defaultAsOf());
  const [range, setRange] = useState(() => defaultRange(defaultAsOf()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  const loadReport = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const asOfYmd = toYmd(asOf);
      const rangeFrom = toYmd(range?.[0]);
      const rangeTo = toYmd(range?.[1]);
      const response = await getOpsFounderReport({
        asOf: asOfYmd,
        from: rangeFrom,
        to: rangeTo,
      });

      if (response?.success === false) {
        setReport(null);
        setError(response.error || "Failed to load report");
        return;
      }

      const data = response?.data ?? response;
      setReport(data);
    } catch (err) {
      setReport(null);
      setError(err?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [asOf, range, canView]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Ops & Founder report access required"
        description="You need reports:dashboard permission to view this tab."
      />
    );
  }

  const metrics = report?.metrics;
  const ai = report?.ai;

  return (
    <ShellRoot data-testid="ops-founder-report-shell">
      <Section>
        <Title level={3} style={{ color: "#005249", marginBottom: 4 }}>
          Ops & Founder snapshot
        </Title>
        <Text type="secondary">
          Daily / range KPIs with AI briefing · counts only · no customer PII
        </Text>
      </Section>

      <FiltersBar>
        <FilterField>
          <span className="filter-label">As-of date</span>
          <DatePicker
            value={asOf}
            onChange={(value) => {
              const next = value || defaultAsOf();
              setAsOf(next);
              setRange((prev) => {
                if (prev?.[0] && prev?.[1]) return prev;
                return defaultRange(next);
              });
            }}
            allowClear={false}
            suffixIcon={<CalendarOutlined />}
          />
        </FilterField>
        <FilterField>
          <span className="filter-label">Range (pipeline & volume)</span>
          <DatePicker.RangePicker
            value={range}
            onChange={(value) => setRange(value || defaultRange(asOf))}
            allowClear={false}
          />
        </FilterField>
        <Button
          type="primary"
          icon={<ReloadOutlined spin={loading} />}
          onClick={loadReport}
          loading={loading}
        >
          Refresh report
        </Button>
      </FiltersBar>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not load Ops & Founder report"
          description={error}
        />
      ) : null}

      {report && report.enabled === false ? (
        <Alert
          type="info"
          showIcon
          message="Report disabled"
          description="Enable features.dashboard.opsFounderReport.enabled in System Settings."
        />
      ) : null}

      {loading && !metrics ? (
        <div data-testid="ops-founder-report-loading">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : null}

      {metrics ? (
        <>
          <Section>
            <Row gutter={[12, 12]}>
              <Col xs={12} md={6}>
                <KpiTile>
                  <div className="kpi-value">{metrics.asOfDay?.leads ?? 0}</div>
                  <div className="kpi-label">Leads on as-of day</div>
                </KpiTile>
              </Col>
              <Col xs={12} md={6}>
                <KpiTile>
                  <div className="kpi-value">{metrics.asOfDay?.quotes ?? 0}</div>
                  <div className="kpi-label">Quotes on as-of day</div>
                </KpiTile>
              </Col>
              <Col xs={12} md={6}>
                <KpiTile>
                  <div className="kpi-value">{metrics.asOfDay?.orders ?? 0}</div>
                  <div className="kpi-label">Orders on as-of day</div>
                </KpiTile>
              </Col>
              <Col xs={12} md={6}>
                <KpiTile>
                  <div className="kpi-value">
                    {metrics.seasonBriefing?.upcoming?.daysUntil != null
                      ? `${metrics.seasonBriefing.upcoming.daysUntil}d`
                      : "—"}
                  </div>
                  <div className="kpi-label">Until next season change</div>
                </KpiTile>
              </Col>
            </Row>
          </Section>

          {metrics.seasonBriefing ? (
            <Section>
              <SeasonOpsBrief briefing={metrics.seasonBriefing} />
            </Section>
          ) : null}

          <Section>
            <SlotCard
              title={
                <Space>
                  <RobotOutlined />
                  AI summary
                  {ai?.status ? (
                    <Tag
                      color={
                        ai.status === "ready"
                          ? "green"
                          : ai.status === "unavailable"
                            ? "default"
                            : "red"
                      }
                    >
                      {ai.status}
                    </Tag>
                  ) : null}
                </Space>
              }
            >
              {ai?.status === "ready" && ai.summary ? (
                <Paragraph style={{ marginBottom: 0 }}>{ai.summary}</Paragraph>
              ) : (
                <Alert
                  type={ai?.status === "error" ? "error" : "warning"}
                  showIcon
                  message={
                    ai?.error ||
                    "AI summary unavailable — metrics below still reflect live aggregates. Configure AI_LLM_* ENV to enable."
                  }
                />
              )}
              {ai?.highlights?.length ? (
                <Space direction="vertical" size={4} style={{ marginTop: 12 }}>
                  {ai.highlights.map((item) => (
                    <Text key={item} type="secondary">
                      · {item}
                    </Text>
                  ))}
                </Space>
              ) : null}
            </SlotCard>
          </Section>

          <Section>
            <Title level={4} style={{ color: "#005249" }}>
              As-of vs trailing windows
            </Title>
            <Row gutter={[12, 12]}>
              {[
                { key: "leads", label: "Leads" },
                { key: "quotes", label: "Quotes" },
                { key: "orders", label: "Orders" },
              ].map((entity) => (
                <Col xs={24} md={8} key={entity.key}>
                  <SlotCard title={entity.label} size="small">
                    <Text>
                      As-of {metrics.asOfDay?.[entity.key] ?? 0} · 7d{" "}
                      {metrics.windows?.d7?.[entity.key] ?? 0} · 30d{" "}
                      {metrics.windows?.d30?.[entity.key] ?? 0}
                    </Text>
                    <br />
                    <Text type="secondary">
                      Range {metrics.windows?.range?.[entity.key] ?? 0} · vs prior
                      day {metrics.asOfDay?.vsPreviousDay?.[entity.key] ?? "0"}
                    </Text>
                  </SlotCard>
                </Col>
              ))}
            </Row>
          </Section>

          <Section>
            <SlotCard title="Created volume — last 14 days (UTC)">
              <SimpleTrendChart trend={metrics.trend} />
            </SlotCard>
          </Section>

          <Section>
            <Title level={4} style={{ color: "#005249" }}>
              Pipeline health (created in selected range)
            </Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <SlotCard title="Quote statuses" size="small">
                  <StatusTable rows={metrics.pipeline?.quoteStatuses} />
                </SlotCard>
              </Col>
              <Col xs={24} lg={12}>
                <SlotCard title="Lead statuses" size="small">
                  <StatusTable rows={metrics.pipeline?.leadStatuses} />
                </SlotCard>
              </Col>
              <Col xs={24}>
                <SlotCard title="Order statuses" size="small">
                  <StatusTable rows={metrics.pipeline?.orderStatuses} />
                </SlotCard>
              </Col>
            </Row>
          </Section>

          <Section>
            <Title level={4} style={{ color: "#005249" }}>
              Founder focus
            </Title>
            {ai?.founderPriorities?.length ? (
              <Table
                size="small"
                pagination={false}
                rowKey={(row) => `${row.priority}-${row.title}`}
                dataSource={ai.founderPriorities}
                columns={[
                  {
                    title: "Priority",
                    dataIndex: "priority",
                    width: 90,
                    render: (value) => (
                      <Tag color={priorityColor(value)}>{value}</Tag>
                    ),
                  },
                  { title: "Action", dataIndex: "title" },
                  { title: "Why", dataIndex: "detail" },
                ]}
              />
            ) : (
              <Empty description="No AI priorities yet" />
            )}
          </Section>

          <Section>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <SlotCard title="Move dates">
                  <Text>
                    Leads move as-of: {metrics.moves?.leadMovesAsOf ?? 0} · next
                    7d: {metrics.moves?.leadMovesNext7 ?? 0}
                  </Text>
                  <br />
                  <Text>
                    Orders move as-of: {metrics.moves?.orderMovesAsOf ?? 0} · next
                    7d: {metrics.moves?.orderMovesNext7 ?? 0}
                  </Text>
                  <br />
                  <Text type="secondary">
                    Leads with move day ≥21 this month:{" "}
                    {metrics.moves?.leadMovesMonthEndThisMonth ?? 0}
                  </Text>
                </SlotCard>
              </Col>
              <Col xs={24} md={12}>
                <SlotCard
                  title={
                    <Space>
                      Pricing flags
                      <Tag
                        color={
                          metrics.pricingFlags?.automation ? "green" : "default"
                        }
                      >
                        {metrics.pricingFlags?.automation
                          ? "Auto ON"
                          : "Auto OFF"}
                      </Tag>
                    </Space>
                  }
                >
                  <Space wrap>
                    <Tag
                      color={
                        metrics.pricingFlags?.corridors ? "green" : "default"
                      }
                    >
                      Corridors{" "}
                      {metrics.pricingFlags?.corridors ? "ON" : "OFF"}
                    </Tag>
                    <Tag
                      color={
                        metrics.pricingFlags?.seasonLive ? "green" : "orange"
                      }
                    >
                      Season{" "}
                      {metrics.pricingFlags?.seasonLive
                        ? "LIVE"
                        : "calendar only"}
                    </Tag>
                    <Tag>
                      Exclusive{" "}
                      {metrics.pricingFlags?.exclusive ? "ON" : "OFF"}
                    </Tag>
                  </Space>
                  <InlineMeta>
                    <Text type="secondary">
                      Suggested-ready quotes:{" "}
                      {metrics.pricingFlags?.suggestedReady ?? 0}
                    </Text>
                  </InlineMeta>
                </SlotCard>
              </Col>
            </Row>
          </Section>

          <Section>
            <SlotCard title="Ops checklist">
              {ai?.opsChecklist?.length ? (
                ai.opsChecklist.map((item) => (
                  <ChecklistItem key={item}>· {item}</ChecklistItem>
                ))
              ) : (
                <Empty description="No AI checklist yet" />
              )}
            </SlotCard>
          </Section>

          <Section>
            <Title level={4} style={{ color: "#005249" }}>
              Book of business
            </Title>
            <Row gutter={[10, 10]}>
              {[
                ["Customers", metrics.totals?.customers],
                ["Leads", metrics.totals?.leads],
                ["Quotes", metrics.totals?.quotes],
                ["Orders", metrics.totals?.orders],
                ["Invoices", metrics.totals?.invoices],
              ].map(([label, value]) => (
                <Col xs={12} md={4} key={label}>
                  <KpiTile>
                    <div className="kpi-value">{value ?? 0}</div>
                    <div className="kpi-label">{label}</div>
                  </KpiTile>
                </Col>
              ))}
            </Row>
          </Section>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Generated {report.generatedAt || "—"} · as-of {report.asOf} · range{" "}
            {report.range?.from} → {report.range?.to}. Quote→order uses
            same-window creates, not cohort conversion.
          </Text>
        </>
      ) : null}
    </ShellRoot>
  );
};

function StatusTable({ rows = [] }) {
  if (!rows?.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No rows" />;
  }
  return (
    <Table
      size="small"
      pagination={false}
      rowKey={(row) => row.status}
      dataSource={rows}
      columns={[
        { title: "Status", dataIndex: "status" },
        { title: "Count", dataIndex: "n", width: 90 },
      ]}
    />
  );
}

export default OpsFounderReportShell;
